/**
 * useMobileViewport - Complete mobile viewport and keyboard handling
 * 
 * Provides a unified solution for mobile web app viewport management with:
 * - Visual Viewport API for keyboard detection
 * - Browser-specific handling (iOS Safari vs Chrome/Android)
 * - Safe area support for modern devices
 * - Smooth transform-based animations
 * - Z-index management system
 * 
 * @returns {Object} Viewport state and utilities
 */
import { useState, useEffect, useRef, useCallback } from 'react';

// Z-index system for consistent layering
export const Z_INDEX = {
  base: 0,
  content: 10,
  mobileInput: 100,        // Input container on mobile
  floatingButtons: 110,    // FABs, scroll-to-bottom
  mobileTopBanner: 120,    // Fixed headers
  sidePanel: 130,          // Chat history, stats panels
  dropdown: 140,           // Dropdowns, select menus
  modal: 150,              // Modals and overlays
  tooltip: 160,            // Tooltips, popovers
  notification: 170,       // Toast notifications
  critical: 9999           // Emergency/critical UI only
};

export const useMobileViewport = () => {
  // Core viewport state
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    visualHeight: window.innerHeight,
    keyboardHeight: 0,
    isKeyboardVisible: false,
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  });

  // Browser/device detection
  const [deviceInfo, setDeviceInfo] = useState({
    isIOS: false,
    isAndroid: false,
    isSafari: false,
    isChrome: false,
    hasNotch: false,
    hasVisualViewport: false,
    hasVirtualKeyboard: false
  });

  // Safe area insets
  const [safeArea, setSafeArea] = useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  });

  // Refs for performance
  const rafRef = useRef();
  const lastViewportHeight = useRef(window.innerHeight);
  const inputFocusedRef = useRef(false);

  // Initialize device detection
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);
    const isSafari = /safari/.test(ua) && !/chrome/.test(ua);
    const isChrome = /chrome/.test(ua) && !/edge/.test(ua);

    // Check for notch (iPhone X and later)
    const hasNotch = isIOS && window.screen.height >= 812;

    setDeviceInfo({
      isIOS,
      isAndroid,
      isSafari,
      isChrome,
      hasNotch,
      hasVisualViewport: !!window.visualViewport,
      hasVirtualKeyboard: 'virtualKeyboard' in navigator
    });

    // Set up CSS custom properties for safe areas
    updateSafeAreaInsets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update safe area insets
  const updateSafeAreaInsets = useCallback(() => {
    const styles = getComputedStyle(document.documentElement);
    const top = parseInt(styles.getPropertyValue('--sat') || getComputedStyle(document.documentElement).paddingTop || '0');
    const right = parseInt(styles.getPropertyValue('--sar') || getComputedStyle(document.documentElement).paddingRight || '0');
    const bottom = parseInt(styles.getPropertyValue('--sab') || getComputedStyle(document.documentElement).paddingBottom || '0');
    const left = parseInt(styles.getPropertyValue('--sal') || getComputedStyle(document.documentElement).paddingLeft || '0');

    setSafeArea({ top, right, bottom, left });

    // Update CSS variables
    document.documentElement.style.setProperty('--safe-area-top', `${top}px`);
    document.documentElement.style.setProperty('--safe-area-right', `${right}px`);
    document.documentElement.style.setProperty('--safe-area-bottom', `${bottom}px`);
    document.documentElement.style.setProperty('--safe-area-left', `${left}px`);
  }, []);

  // Main viewport change handler
  const handleViewportChange = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      let visualHeight = height;
      let keyboardHeight = 0;
      let isKeyboardVisible = false;

      // Use Visual Viewport API if available
      if (window.visualViewport) {
        visualHeight = window.visualViewport.height;
        keyboardHeight = height - visualHeight;
        
        // Consider keyboard visible if it takes more than 50px
        isKeyboardVisible = keyboardHeight > 50 && inputFocusedRef.current;
      } else {
        // Fallback: detect significant height changes
        const heightDiff = lastViewportHeight.current - height;
        if (heightDiff > 100 && inputFocusedRef.current) {
          keyboardHeight = heightDiff;
          isKeyboardVisible = true;
          visualHeight = height;
        }
      }

      // Update state
      setViewport(prev => ({
        ...prev,
        width,
        height,
        visualHeight,
        keyboardHeight,
        isKeyboardVisible,
        orientation: width > height ? 'landscape' : 'portrait'
      }));

      // Update CSS variables for use in styles
      document.documentElement.style.setProperty('--viewport-height', `${height}px`);
      document.documentElement.style.setProperty('--visual-height', `${visualHeight}px`);
      document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
      document.documentElement.style.setProperty('--available-height', `${visualHeight}px`);

      lastViewportHeight.current = height;
    });
  }, []);

  // Track input focus state
  const handleFocusIn = useCallback((e) => {
    if (e.target.matches('input, textarea, [contenteditable]')) {
      inputFocusedRef.current = true;
      setTimeout(handleViewportChange, 100); // Small delay for keyboard animation
    }
  }, [handleViewportChange]);

  const handleFocusOut = useCallback(() => {
    inputFocusedRef.current = false;
    setTimeout(handleViewportChange, 300); // Longer delay for keyboard dismissal
  }, [handleViewportChange]);

  // Set up event listeners
  useEffect(() => {
    // Visual Viewport API
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    }

    // Fallback events
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    
    // Focus tracking
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    // Initial measurement
    handleViewportChange();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
        window.visualViewport.removeEventListener('scroll', handleViewportChange);
      }
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleViewportChange, handleFocusIn, handleFocusOut]);

  // Utility: Get mobile input container styles
  const getMobileInputStyles = useCallback((options = {}) => {
    const { additionalPadding = 0 } = options;
    
    const baseStyles = {
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: Z_INDEX.mobileInput,
      // Use transform for smooth animations
      transform: `translateY(${viewport.isKeyboardVisible ? -viewport.keyboardHeight : 0}px)`,
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      willChange: 'transform',
      // Include safe areas
      paddingBottom: `calc(${safeArea.bottom}px + ${additionalPadding}px)`,
      paddingLeft: `${safeArea.left}px`,
      paddingRight: `${safeArea.right}px`
    };

    // iOS Safari specific adjustments
    if (deviceInfo.isIOS && deviceInfo.isSafari) {
      // iOS Safari shrinks viewport, so we don't need transform
      baseStyles.transform = 'translateY(0)';
      baseStyles.position = 'fixed';
    }

    return baseStyles;
  }, [viewport, safeArea, deviceInfo]);

  // Utility: Get content area styles (messages list)
  const getContentAreaStyles = useCallback((options = {}) => {
    const { hasTopBanner = true, hasBottomInput = true } = options;
    
    const topOffset = hasTopBanner ? 60 : 0; // Adjust based on your top banner height
    const bottomOffset = hasBottomInput ? 80 : 0; // Adjust based on your input height
    
    return {
      position: 'absolute',
      top: `${topOffset + safeArea.top}px`,
      left: `${safeArea.left}px`,
      right: `${safeArea.right}px`,
      bottom: `${bottomOffset + safeArea.bottom}px`,
      // Adjust height when keyboard is visible
      height: viewport.isKeyboardVisible 
        ? `calc(${viewport.visualHeight}px - ${topOffset + bottomOffset}px)`
        : 'auto',
      transition: 'height 0.3s ease-out',
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
    };
  }, [viewport, safeArea]);

  // Utility: Get floating button styles
  const getFloatingButtonStyles = useCallback((position = 'bottom-right', options = {}) => {
    const { offset = 16 } = options;
    
    const styles = {
      position: 'fixed',
      zIndex: Z_INDEX.floatingButtons,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    };

    // Position variations
    switch (position) {
      case 'bottom-right':
        styles.bottom = `${offset + safeArea.bottom + (viewport.isKeyboardVisible ? viewport.keyboardHeight : 0)}px`;
        styles.right = `${offset + safeArea.right}px`;
        break;
      case 'bottom-left':
        styles.bottom = `${offset + safeArea.bottom + (viewport.isKeyboardVisible ? viewport.keyboardHeight : 0)}px`;
        styles.left = `${offset + safeArea.left}px`;
        break;
      case 'bottom-center':
        styles.bottom = `${offset + safeArea.bottom + (viewport.isKeyboardVisible ? viewport.keyboardHeight : 0)}px`;
        styles.left = '50%';
        styles.transform = 'translateX(-50%)';
        break;
      default:
        // Default to bottom-right if position is not recognized
        styles.bottom = `${offset + safeArea.bottom + (viewport.isKeyboardVisible ? viewport.keyboardHeight : 0)}px`;
        styles.right = `${offset + safeArea.right}px`;
        break;
    }

    return styles;
  }, [viewport, safeArea]);

  return {
    viewport,
    deviceInfo,
    safeArea,
    isKeyboardVisible: viewport.isKeyboardVisible,
    keyboardHeight: viewport.keyboardHeight,
    isMobile: viewport.width < 768,
    getMobileInputStyles,
    getContentAreaStyles,
    getFloatingButtonStyles,
    Z_INDEX
  };
};

// CSS utilities to add to your global styles
export const viewportCSS = `
  /* CSS Variables for viewport dimensions */
  :root {
    --viewport-height: 100vh;
    --visual-height: 100vh;
    --keyboard-height: 0px;
    --available-height: 100vh;
    
    /* Safe area insets */
    --sat: env(safe-area-inset-top);
    --sar: env(safe-area-inset-right);
    --sab: env(safe-area-inset-bottom);
    --sal: env(safe-area-inset-left);
    
    --safe-area-top: 0px;
    --safe-area-right: 0px;
    --safe-area-bottom: 0px;
    --safe-area-left: 0px;
  }
  
  /* Prevent overscroll bounce on iOS */
  html {
    height: 100%;
  }
  
  body {
    height: 100%;
    overscroll-behavior: none;
    -webkit-overflow-scrolling: touch;
  }
  
  /* Only apply fixed positioning to chat interface */
  body.chat-interface {
    position: fixed;
    width: 100%;
    overflow: hidden;
  }
  
  /* Smooth transitions for keyboard appearance */
  .keyboard-transition {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

// Export viewport meta tag content
export const viewportMetaContent = "width=device-width, initial-scale=1.0, interactive-widget=resizes-content, viewport-fit=cover";